
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { DatePicker } from '@/components/ui/date-picker';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Calendar, FileText, Users } from 'lucide-react';
import { useScoutTasks } from '@/hooks/useScoutTasks';
import { useScoutTaskAssignments } from '@/hooks/useScoutTaskAssignments';
import { useScoutAthletes } from '@/hooks/useScoutAthletes';
import { toast } from 'sonner';

interface AssignTaskModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAssigned: () => void;
}

interface AthleteAssignment {
  athleteId: string;
  isSelected: boolean;
  dueDate?: Date;
  notes: string;
}

const AssignTaskModal: React.FC<AssignTaskModalProps> = ({
  isOpen,
  onClose,
  onAssigned,
}) => {
  const { tasks: taskTemplates, isLoading: tasksLoading } = useScoutTasks();
  const { createAssignment } = useScoutTaskAssignments();
  const { athletes, isLoading: athletesLoading } = useScoutAthletes();
  
  const [selectedTaskId, setSelectedTaskId] = useState<string>('');
  const [assignments, setAssignments] = useState<Record<string, AthleteAssignment>>({});
  const [isAssigning, setIsAssigning] = useState(false);

  useEffect(() => {
    if (athletes.length > 0) {
      const initialAssignments: Record<string, AthleteAssignment> = {};
      athletes.forEach((athlete) => {
        initialAssignments[athlete.id] = {
          athleteId: athlete.id,
          isSelected: false,
          dueDate: undefined,
          notes: '',
        };
      });
      setAssignments(initialAssignments);
    } else {
      setAssignments({});
    }
  }, [athletes]);

  const handleAthleteToggle = (athleteId: string, checked: boolean) => {
    setAssignments((prev) => ({
      ...prev,
      [athleteId]: {
        ...prev[athleteId],
        isSelected: checked,
        dueDate: checked ? prev[athleteId]?.dueDate || new Date(Date.now() + 7 * 24 * 60 * 60 * 1000) : undefined,
      },
    }));
  };

  const handleDueDateChange = (athleteId: string, date?: Date) => {
    setAssignments((prev) => ({
      ...prev,
      [athleteId]: {
        ...prev[athleteId],
        dueDate: date,
      },
    }));
  };

  const handleNotesChange = (athleteId: string, notes: string) => {
    setAssignments((prev) => ({
      ...prev,
      [athleteId]: {
        ...prev[athleteId],
        notes,
      },
    }));
  };

  const getSelectedAthletes = () => {
    return Object.values(assignments).filter((a) => a.isSelected);
  };

  const isValid = () => {
    const selected = getSelectedAthletes();
    return selectedTaskId && selected.length > 0 && selected.every((a) => a.dueDate);
  };

  const handleAssign = async () => {
    if (!selectedTaskId) {
      toast.error('Please select a task template');
      return;
    }

    const selected = getSelectedAthletes();
    if (selected.length === 0) {
      toast.error('Please select at least one athlete');
      return;
    }

    const missingDueDates = selected.filter(assignment => !assignment.dueDate);
    if (missingDueDates.length > 0) {
      toast.error('Please set due dates for all selected athletes');
      return;
    }

    setIsAssigning(true);
    try {
      let successCount = 0;
      let duplicateCount = 0;
      const duplicateAthletes: string[] = [];
      
      for (const assignment of selected) {
        const result = await createAssignment({
          task_id: selectedTaskId,
          athlete_profile_id: assignment.athleteId,
          due_date: assignment.dueDate!.toISOString().split('T')[0],
          notes: assignment.notes.trim() || undefined,
          status: 'new'
        });
        
        if (result.success) {
          successCount++;
        } else if (result.isDuplicate) {
          duplicateCount++;
          // Find athlete name for duplicate message
          const athlete = athletes.find(a => a.id === assignment.athleteId);
          if (athlete) duplicateAthletes.push(athlete.name);
        }
      }

      // Show appropriate success/warning messages
      if (successCount > 0 && duplicateCount === 0) {
        toast.success(`Successfully assigned task to ${successCount} athlete(s)`);
      } else if (successCount > 0 && duplicateCount > 0) {
        toast.success(`Successfully assigned task to ${successCount} athlete(s). ${duplicateCount} athlete(s) already had this task assigned.`);
      } else if (duplicateCount > 0) {
        toast.warning(`Task already assigned to: ${duplicateAthletes.join(', ')}`);
      } else {
        toast.error('Failed to assign tasks');
      }

      // Close modal and reset if at least one assignment was successful or all were duplicates
      if (successCount > 0 || duplicateCount === selected.length) {
        onAssigned();
        onClose();
        
        // Reset form
        setSelectedTaskId('');
        setAssignments(prev => {
          const reset = { ...prev };
          Object.keys(reset).forEach(key => {
            reset[key] = { ...reset[key], isSelected: false, dueDate: undefined, notes: '' };
          });
          return reset;
        });
      }
    } catch (err) {
      console.error('Error assigning tasks:', err);
      toast.error('Failed to assign tasks');
    } finally {
      setIsAssigning(false);
    }
  };

  const selectedTask = taskTemplates.find(task => task.id === selectedTaskId);

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'destructive';
      case 'normal': return 'default';
      case 'low': return 'secondary';
      default: return 'outline';
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" /> Assign Task to Athletes
          </DialogTitle>
          <DialogDescription>
            Select a task template and assign it to one or more athletes with custom due dates and notes.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Task Template Selection */}
          <div className="space-y-3">
            <Label>Select Task Template *</Label>
            <Select value={selectedTaskId} onValueChange={setSelectedTaskId}>
              <SelectTrigger>
                <SelectValue placeholder="Choose a task template..." />
              </SelectTrigger>
              <SelectContent>
                {taskTemplates.map((task) => (
                  <SelectItem key={task.id} value={task.id}>
                    <div className="flex items-center gap-2">
                      <span>{task.title}</span>
                      <Badge variant={getPriorityColor(task.priority)} className="text-xs">
                        {task.priority}
                      </Badge>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {selectedTask && (
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h4 className="font-semibold">{selectedTask.title}</h4>
                        <Badge variant={getPriorityColor(selectedTask.priority)}>
                          {selectedTask.priority}
                        </Badge>
                      </div>
                      {selectedTask.description && (
                        <p className="text-sm text-muted-foreground">{selectedTask.description}</p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Athletes Selection */}
          {selectedTaskId && (
            <div>
              <h3 className="text-lg font-semibold mb-4">Select Athletes</h3>
              {athletesLoading ? (
                <div className="space-y-3">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="h-16 bg-gray-100 rounded animate-pulse" />
                  ))}
                </div>
              ) : (
                <div className="space-y-4">
                  {athletes.map((athlete) => (
                    <Card key={athlete.id} className="p-4">
                      <div className="space-y-3">
                        <div className="flex items-center space-x-3">
                          <Checkbox
                            checked={assignments[athlete.id]?.isSelected || false}
                            onCheckedChange={(checked) => handleAthleteToggle(athlete.id, checked as boolean)}
                          />
                          <div className="flex-1">
                            <div className="font-medium">{athlete.name}</div>
                            <div className="text-sm text-gray-500">{athlete.email}</div>
                            {athlete.grad_year && (
                              <div className="text-xs text-gray-400">Class of {athlete.grad_year}</div>
                            )}
                          </div>
                        </div>

                        {assignments[athlete.id]?.isSelected && (
                          <div className="ml-7 space-y-3">
                            <div>
                              <label className="text-sm font-medium flex items-center gap-1 mb-1">
                                <Calendar className="h-3 w-3" /> Due Date *
                              </label>
                              <DatePicker
                                date={assignments[athlete.id]?.dueDate}
                                setDate={(date) => handleDueDateChange(athlete.id, date)}
                                placeholder="Select due date"
                                className="w-full"
                              />
                            </div>
                            <div>
                              <label className="text-sm font-medium flex items-center gap-1 mb-1">
                                <FileText className="h-3 w-3" /> Notes (optional)
                              </label>
                              <Textarea
                                value={assignments[athlete.id]?.notes || ''}
                                onChange={(e) => handleNotesChange(athlete.id, e.target.value)}
                                placeholder="Add any specific notes for this athlete..."
                                className="min-h-[60px]"
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </Card>
                  ))}

                  {athletes.length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                      No athletes assigned to you yet
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex justify-between items-center pt-6 border-t">
          <div className="text-sm text-gray-600">
            {getSelectedAthletes().length} athlete(s) selected
          </div>
          <div className="flex space-x-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={handleAssign} disabled={!isValid() || isAssigning}>
              {isAssigning ? 'Assigning...' : 'Assign Task'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AssignTaskModal;
